import { QueryClient } from '@tanstack/react-query';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import { contract } from '@clarity/contract';
import { getAuthToken } from './session.js';

/**
 * The API base URL.
 *
 * Defaults to the SAME-ORIGIN path `/api`, which the Vite dev server proxies to
 * the Fastify server (see `vite.config.ts`). Going through the dev-server proxy
 * means the browser only ever talks to the page's own origin (port 5004) — same
 * protocol, no CORS preflight, and no mixed-content block when the UI is served
 * over HTTPS via a forwarded port / tunnel. This is what makes it work on a
 * remote dev box where a direct cross-port `http://localhost:3000` call from an
 * `https://` page is blocked.
 *
 * Override with `VITE_API_BASE_URL` to point at an explicit server URL (e.g. a
 * deployed backend) when not using the dev proxy.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/**
 * Resolve a stored catalog image path (a catalog item's `imagePath`) to a URL the
 * browser can load. The server serves images at `/catalog-images/*` behind the
 * same base URL as the API (so the dev proxy / forwarded origin applies), and the
 * storage backend (local FS now, S3/GCS later) is hidden behind that route.
 * Returns `undefined` when there is no stored image.
 */
export function catalogImageUrl(imagePath: string | null | undefined): string | undefined {
  if (!imagePath) return undefined;
  const base = API_BASE_URL.replace(/\/$/, '');
  const clean = imagePath.replace(/^\//, '');
  return `${base}/catalog-images/${clean}`;
}

/**
 * The typed ts-rest + TanStack Query client, bound to the single-source-of-truth
 * `contract` (Req 6.5). Every resource becomes a hooks container — e.g.
 * `tsr.clients.list.useQuery(...)`, `tsr.auth.login.useMutation(...)` — so
 * loading / error / caching all come from the query layer rather than hand-rolled
 * fetch state.
 *
 * The `Authorization` header is a function so it is re-read on every request from
 * the live session token (`getAuthToken`); logging in/out therefore changes auth
 * without rebuilding the client. The server verifies the JWT — the client never
 * does (Req 2.3/6.6).
 */
export const tsr = initTsrReactQuery(contract, {
  baseUrl: API_BASE_URL,
  baseHeaders: {
    authorization: () => {
      const token = getAuthToken();
      return token ? `Bearer ${token}` : '';
    },
  },
});

/**
 * The TanStack Query client. `retry: false` keeps error states deterministic
 * and fast — a failed query surfaces immediately rather than silently retrying,
 * which matters for the role-gated 401/403 paths (Req 6.5).
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
